import * as T from "three/webgpu";
import Sizes from "./Utils/Sizes";
import Time from "./Utils/Time";
import Camera from "./Camera";
import Renderer from "./Renderer";
import World from "./World/World";
import Resources from "./Utils/Resources";
import sources from "./sources";
import Debug from "./Utils/Debug";
import Input from "./Utils/Input";
import Audio from "./Utils/Audio";
import { color, screenUV, hue, reflector, time, Fn, vec2, length, atan, float, sin, cos, vec3, sub, mul, pow, blendDodge, normalWorldGeometry } from 'three/tsl';

let instance = null;

export default class Experience {
  constructor(canvas) {
    //global access
    // window.experience = this;

    //singleton
    if (instance) return instance;
    instance = this;

    //options
    this.canvas = canvas;

    //setup
    this.sizes = new Sizes();
    this.time = new Time();
    this.debug = new Debug();
    this.input = new Input();
    this.audio = new Audio();
    this.scene = new T.Scene();
    this.resources = new Resources(sources);
    this.camera = new Camera();
    this.renderer = new Renderer();
    this.world = new World();

    this.setSceneBackground();

    // resize event
    this.sizes.on("resize", () => {
      this.resize();
    });

    //time tick event
    this.time.on("tick", () => {
      this.update();
    });
  }

  setSceneBackground() {
    const lightSpeed = /*#__PURE__*/ Fn( ( [ suv_immutable ] ) => {

      // forked from https://www.shadertoy.com/view/7ly3D1

      const suv = vec2( suv_immutable );
      const uv = vec2( length( suv ), atan( suv.y, suv.x ) );
      const offset = float( float( .1 ).mul( sin( uv.y.mul( 10. ).sub( time.mul( .6 ) ) ) ).mul( cos( uv.y.mul( 48. ).add( time.mul( .3 ) ) ) ).mul( cos( uv.y.mul( 3.7 ).add( time ) ) ) );
      const rays = vec3( vec3( sin( uv.y.mul( 150. ).add( time ) ).mul( .5 ).add( .5 ) ).mul( vec3( sin( uv.y.mul( 80. ).sub( time.mul( 0.6 ) ) ).mul( .5 ).add( .5 ) ) ).mul( vec3( sin( uv.y.mul( 45. ).add( time.mul( 0.8 ) ) ).mul( .5 ).add( .5 ) ) ).mul( vec3( sub( 1., cos( uv.y.add( mul( 22., time ).sub( pow( uv.x.add( offset ), .3 ).mul( 60. ) ) ) ) ) ) ).mul( vec3( uv.x.mul( 2. ) ) ) );

      return rays;

    } ).setLayout( {
      name: 'lightSpeed',
      type: 'vec3',
      inputs: [
        { name: 'suv', type: 'vec2' }
      ]
    } );

    // scene

    const scene = this.scene;

    // background

    const coloredVignette = screenUV.distance( .5 ).mix( hue( color( 0xff5a8c ), time.mul( .08 ) ), hue( color( 0x1a0a3a ), time.mul( .25 ) ) );
    const lightSpeedEffect = lightSpeed( normalWorldGeometry ).clamp();
    const lightSpeedSky = normalWorldGeometry.y.remapClamp( - .1, 1 ).mix( 0, lightSpeedEffect );
    const composedBackground = blendDodge( coloredVignette, lightSpeedSky );

    scene.backgroundNode = composedBackground;
  }

  resize() {
    this.camera.resize();
    this.renderer.resize();
  }

  update() {
    this.camera.update();
    this.world.update();
    this.renderer.update();
  }

  destroy() {
    this.sizes.off("resize");
    this.time.off("tick");

    // traverse
    this.scene.traverse((child) => {
      if (child instanceof T.Mesh) {
        child.geometry.dispose();

        for (const key in child.material) {
          const value = child.material[key];
          if (value && typeof value.dispose === "function") {
            value.dispose();
          }
        }
      }
    });

    this.camera.controls.dispose();
    this.renderer.instance.dispose();

    if (this.debug.active) this.debug.ui.destroy();
  }
}
